// Who wrote a support ticket message - the backend's author_type on every
// ticket-message payload (staff thread, portal thread, Accounts Centre /
// customer ticket lists). Staff messages include public ones with no send
// behind them (e.g. comments imported from Gaiia), so "no communication log"
// doesn't mean the customer wrote it.
export type TicketMessageAuthorType = 'staff' | 'customer' | 'system';

/** The fields of a raw (snake_case) ticket message these helpers read. */
export interface RawTicketMessageAuthor {
  author_type?: string | null;
  author_user_id?: number | null;
  author_name?: string | null;
  is_internal?: boolean | null;
  communication_log_id?: number | null;
}

/**
 * A raw (snake_case) ticket message's author type. Older payloads without
 * author_type fall back to the previous inference: internal notes, sends
 * (a communication log) and messages with a staff author_user_id are staff,
 * anything else is the customer.
 */
export function messageAuthorType(
  apiMessage: RawTicketMessageAuthor | null | undefined
): TicketMessageAuthorType {
  const type = apiMessage?.author_type;
  if (type === 'staff' || type === 'customer' || type === 'system') return type;
  if (apiMessage?.is_internal || apiMessage?.communication_log_id || apiMessage?.author_user_id) {
    return 'staff';
  }
  return 'customer';
}

/**
 * Display name for a raw ticket message.
 * - 'staff' view (Support / Portal Management): the stored author name, else
 *   Staff (internal note) / Customer / System / Support.
 * - 'portal' view (the customer's own portal): "You" for the customer's
 *   messages, else the author name or "Support".
 */
export function messageAuthorLabel(
  apiMessage: RawTicketMessageAuthor | null | undefined,
  view: 'staff' | 'portal' = 'staff'
): string {
  const type = messageAuthorType(apiMessage);
  if (view === 'portal') {
    return type === 'customer' ? 'You' : apiMessage?.author_name || 'Support';
  }
  if (apiMessage?.author_name) return apiMessage.author_name;
  if (apiMessage?.is_internal) return 'Staff';
  return type === 'customer' ? 'Customer' : type === 'system' ? 'System' : 'Support';
}
